import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  Typography
} from '@mui/material';

interface DataPreviewTableProps {
  columns: string[];
  rows: Record<string, any>[];
  maxRows?: number;
  maxColumnsToShow?: number;
}

const DataPreviewTable: React.FC<DataPreviewTableProps> = ({
  columns,
  rows,
  maxRows = 10,
  maxColumnsToShow = 10
}) => {
  const previewRows = rows.slice(0, maxRows);
  const displayColumns = columns.slice(0, maxColumnsToShow);
  const hasMoreColumns = columns.length > maxColumnsToShow;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
        Preview of imported data
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '200px',
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
              {displayColumns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600 }}>
                  {col}
                </TableCell>
              ))}
              {hasMoreColumns && (
                <TableCell sx={{ fontWeight: 600 }}>...</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell sx={{ color: 'text.secondary' }}>
                  {rowIndex + 1}
                </TableCell>
                {displayColumns.map((col) => {
                  const val = row[col];
                  const str = val != null ? String(val) : '';
                  return (
                    <TableCell key={col}>
                      {`${str.substring(0, 40)}${str.length > 40 ? '...' : ''}`}
                    </TableCell>
                  );
                })}
                {hasMoreColumns && <TableCell>...</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {(rows.length > maxRows || columns.length > maxColumnsToShow) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {rows.length > maxRows && columns.length > maxColumnsToShow
            ? `Showing first ${maxRows} rows, ${maxColumnsToShow} of ${columns.length} columns`
            : rows.length > maxRows
              ? `Showing first ${maxRows} of ${rows.length} rows`
              : `Showing ${maxColumnsToShow} of ${columns.length} columns`}
        </Typography>
      )}
    </Box>
  );
};

export default DataPreviewTable;
